import clsx from 'clsx'
import { ChevronRight, Skull } from 'lucide-react'
import { chartPartOfWholeColor } from '@/config/chartTheme'
import { useDrillDown } from '@/components/dashboards/useDrillDown'
import {
  compactCurrency,
  drillForIds,
  type FunnelResponse,
} from '@/services/dashboardAnalytics'

interface FunnelStripProps {
  data: FunnelResponse | null | undefined
  /** 'row' = one horizontal strip (Explorer header); 'column' = a ranked list
   *  with bars, for a 2x2 board panel. */
  orientation?: 'row' | 'column'
  /** Prefix for every drill title — the cohort the funnel was asked for. */
  scopeLabel?: string
  className?: string
}

/** Stage names short enough for a strip cell; the full name is the tooltip. */
const STAGE_SHORT: Record<string, string> = {
  'Pre-screen Indication': 'Pre-screen',
  'Post-screen Quote': 'Quote',
  'Term Sheet Issued': 'TS issued',
  'Term Sheet Signed': 'TS signed',
  'IC / Committee Approved': 'IC approved',
}

export function stageShortLabel(label: string): string {
  return STAGE_SHORT[label] ?? label
}

function pct(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '--' : `${value.toFixed(0)}%`
}

function days(value: number | null | undefined, n: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '--'
  return `${Math.round(value)}d${n != null ? ` · n ${n}` : ''}`
}

/**
 * The pipeline funnel: one cell per stage in the platform's stage order with
 * its count, its volume, the share that moved to the next stage and the
 * average days a deal sat in it. Every count and amount opens exactly the
 * deals it was summed from. Dead deals sit apart, with the stage they died at.
 */
export default function FunnelStrip({
  data,
  orientation = 'row',
  scopeLabel,
  className,
}: FunnelStripProps) {
  const { open } = useDrillDown()
  const stages = data?.stages ?? []
  const maxCount = Math.max(1, ...stages.map((stage) => stage.count))
  const title = (label: string) => (scopeLabel ? `${scopeLabel} — ${label}` : label)
  const dead = data?.dead

  if (stages.length === 0) {
    return <p className="px-1 py-3 text-[11px] text-stone-500">No stage on record for this cohort.</p>
  }

  if (orientation === 'column') {
    return (
      <div className={clsx('flex min-h-0 flex-col gap-1', className)}>
        <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,2fr)_56px_64px_52px_64px] gap-x-2 px-1 font-mono text-[8.5px] uppercase tracking-[0.16em] text-stone-600">
          <span>Stage</span>
          <span />
          <span className="text-right">Deals</span>
          <span className="text-right">Volume</span>
          <span className="text-right" title="Share of deals in this stage that reached the next">
            To next
          </span>
          <span className="text-right" title="Average whole days in the stage (24-hour days, weekends count)">
            Dwell
          </span>
        </div>
        {stages.map((stage, index) => {
          const drill = drillForIds(stage.dealIds, title(stage.label))
          return (
            <div
              key={stage.stage}
              className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,2fr)_56px_64px_52px_64px] items-center gap-x-2 rounded px-1 py-[3px] hover:bg-amber-400/[0.03]"
            >
              <span className="truncate text-[11px] text-stone-300" title={stage.label}>
                {stageShortLabel(stage.label)}
              </span>
              <button
                type="button"
                onClick={() => open(drill)}
                disabled={!drill}
                aria-label={`${stage.label}: open ${stage.count} deals`}
                className="relative h-3 rounded-sm bg-white/[0.03] text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50 disabled:cursor-default"
              >
                <span
                  className="absolute inset-y-0 left-0 rounded-sm transition-[width] duration-500"
                  style={{
                    width: `${Math.max(stage.count > 0 ? 3 : 0, (stage.count / maxCount) * 100)}%`,
                    background: chartPartOfWholeColor(index, stages.length),
                  }}
                />
              </button>
              <button
                type="button"
                onClick={() => open(drill)}
                disabled={!drill}
                className="text-right font-mono text-[11.5px] tabular-nums text-stone-100 underline decoration-amber-400/25 decoration-dotted underline-offset-[3px] hover:text-amber-200 disabled:no-underline disabled:text-stone-600"
              >
                {stage.count.toLocaleString('en-US')}
              </button>
              <span className="text-right font-mono text-[10.5px] tabular-nums text-stone-400">
                {compactCurrency(stage.amount)}
              </span>
              <span className="text-right font-mono text-[10.5px] tabular-nums text-stone-400">
                {index === stages.length - 1 ? '' : pct(stage.convToNextPct)}
              </span>
              <span className="text-right font-mono text-[10px] tabular-nums text-stone-500">
                {days(stage.avgDaysInStage, null)}
              </span>
            </div>
          )
        })}
        {dead && (
          <DeadLine dead={dead} onOpen={() => open(drillForIds(dead.dealIds, title('Dead deals')))} />
        )}
      </div>
    )
  }

  return (
    <div className={clsx('flex min-w-0 items-stretch gap-1', className)}>
      {stages.map((stage, index) => {
        const drill = drillForIds(stage.dealIds, title(stage.label))
        const last = index === stages.length - 1
        return (
          <div key={stage.stage} className="flex min-w-0 flex-1 items-stretch">
            <button
              type="button"
              onClick={() => open(drill)}
              disabled={!drill}
              title={drill ? `${stage.label} — open ${stage.count} deals` : stage.label}
              className="group relative flex min-w-0 flex-1 flex-col justify-between overflow-hidden rounded-md border border-amber-400/[0.08] bg-[#0a0a0a] px-2 py-1.5 text-left transition-colors hover:border-amber-400/30 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50 disabled:cursor-default disabled:hover:border-amber-400/[0.08]"
            >
              <span
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-[3px]"
                style={{
                  background: chartPartOfWholeColor(index, stages.length),
                  opacity: 0.35 + 0.65 * (stage.count / maxCount),
                }}
              />
              <span className="truncate font-mono text-[8.5px] uppercase tracking-[0.14em] text-stone-500" title={stage.label}>
                {stageShortLabel(stage.label)}
              </span>
              <span className="mt-0.5 flex items-baseline gap-1.5">
                <span className="font-mono text-[15px] font-semibold tabular-nums text-stone-100 group-hover:text-amber-200">
                  {stage.count.toLocaleString('en-US')}
                </span>
                <span className="truncate font-mono text-[10px] tabular-nums text-stone-400">
                  {compactCurrency(stage.amount)}
                </span>
              </span>
              <span className="truncate font-mono text-[9px] tabular-nums text-stone-600" title="Average whole days in stage">
                {days(stage.avgDaysInStage, stage.avgDaysInStageN)}
              </span>
            </button>
            {!last && (
              <div className="flex w-7 flex-shrink-0 flex-col items-center justify-center text-stone-600">
                <ChevronRight size={11} aria-hidden="true" />
                <span
                  className="font-mono text-[9px] tabular-nums text-stone-400"
                  title={`Share of ${stage.label} deals that reached the next stage`}
                >
                  {pct(stage.convToNextPct)}
                </span>
              </div>
            )}
          </div>
        )
      })}
      {dead && (
        <button
          type="button"
          onClick={() => open(drillForIds(dead.dealIds, title('Dead deals')))}
          disabled={dead.count === 0}
          title={
            dead.count === 0
              ? 'No dead deal is loaded in this partition — Backshop dead deals have not been imported'
              : 'Dead deals — open records'
          }
          className="ml-1 flex w-[88px] flex-shrink-0 flex-col justify-between rounded-md border border-dashed border-stone-700/60 px-2 py-1.5 text-left transition-colors hover:border-stone-500 disabled:cursor-default disabled:hover:border-stone-700/60"
        >
          <span className="flex items-center gap-1 font-mono text-[8.5px] uppercase tracking-[0.14em] text-stone-500">
            <Skull size={9} aria-hidden="true" /> Dead
          </span>
          <span className="font-mono text-[15px] font-semibold tabular-nums text-stone-300">
            {dead.count.toLocaleString('en-US')}
          </span>
          <span className="truncate font-mono text-[9px] text-stone-600">
            {dead.count === 0 ? 'none loaded' : compactCurrency(dead.amount)}
          </span>
        </button>
      )}
    </div>
  )
}

function DeadLine({
  dead,
  onOpen,
}: {
  dead: FunnelResponse['dead']
  onOpen: () => void
}) {
  return (
    <div className="mt-1 flex items-center gap-2 border-t border-amber-400/[0.06] px-1 pt-1.5 text-[10.5px] text-stone-500">
      <Skull size={10} className="flex-shrink-0 text-stone-600" aria-hidden="true" />
      {dead.count === 0 ? (
        <span>No dead deal is loaded in this partition — dead deals from Backshop are not imported yet.</span>
      ) : (
        <>
          <button
            type="button"
            onClick={onOpen}
            className="font-mono tabular-nums text-stone-300 underline decoration-amber-400/25 decoration-dotted underline-offset-[3px] hover:text-amber-200"
          >
            {dead.count.toLocaleString('en-US')} dead
          </button>
          <span className="font-mono tabular-nums">{compactCurrency(dead.amount)}</span>
          <span className="truncate">
            {dead.byStage
              .filter((entry) => entry.count > 0)
              .map((entry) => `${entry.count} at ${entry.stage}`)
              .join(' · ')}
          </span>
        </>
      )}
    </div>
  )
}
